/* eslint-disable react/no-unused-prop-types */
/**
 * Transition: manages callbacks and classnames for enter, exit, mount
 * */
import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import passthroughProps from './utils';

const transitionEndEvent = 'animationend';
export const transitionState = {
  MOUNTED: 'MOUNTED',
  UNMOUNTED: 'UNMOUNTED',
  ENTERING: 'ENTERING',
  ENTERED: 'ENTERED',
  EXITING: 'EXITING',
  EXITED: 'EXITED'
};

export const transitions = {
  enter: 'ENTER',
  exit: 'EXIT',
  none: 'NONE'
};

/** */
// eslint-disable-next-line react/no-unsafe
export default class Transition extends React.Component {
  static propTypes = {
    /** Shows  the component on initial mount if true, only needed if props.transition !== transitions.enter
     * if props.transition === transitions.enter, the component will automatically render on mount */
    // eslint-disable-next-line react/boolean-prop-naming
    renderOnMount: PropTypes.bool,
    // Show the component; triggers the enter or exit animation
    transition: PropTypes.oneOf([
      transitions.enter,
      transitions.exit,
      transitions.none
    ]),
    /** Unmount the component (remove it from the DOM) when it is not shown */
    // eslint-disable-next-line react/boolean-prop-naming
    unmountOnExit: PropTypes.bool,
    /** Callback fired before the 'enter' classes are applied */
    onEnter: PropTypes.func,
    /** Callback fired before the enter css animation finishes */
    onEntering: PropTypes.func,
    /** Callback fired after the 'enter' classes are applied */
    onEntered: PropTypes.func,
    /** Callback fired before the 'exit' classes are applied */
    onExit: PropTypes.func,
    /** Callback fired before the exit css animation finishes */
    onExiting: PropTypes.func,
    /** Callback fired after the 'exited' classes are applied */
    onExited: PropTypes.func,
    /** Callback fired after onExiting is called if unmountOnExit is true */
    onUnmount: PropTypes.func,
    className: PropTypes.string,
    children: PropTypes.element.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    style: PropTypes.object
  };

  static defaultProps = {
    className: '',
    renderOnMount: false,
    transition: '',
    unmountOnExit: false,
    onEnter() {},
    onEntering() {},
    onEntered() {},
    onExit() {},
    onExiting() {},
    onExited() {},
    onUnmount() {},
    style: undefined
  };

  constructor(props) {
    super(props);
    this.state = {
      // if renderOnMount or if we want to apply a enter transition on initial render we need to have MOUNTED status
      status:
        props.renderOnMount || props.transition === transitions.enter
          ? transitionState.MOUNTED
          : transitionState.UNMOUNTED
    };
    this.performEnterAfterUpdate = false;
    this.listeners = {};
  }

  componentDidMount() {
    if (this.props.transition === transitions.enter) {
      this.performEnter(this.props);
    } else if (
      this.props.transition === transitions.exit &&
      this.state.status === transitionState.MOUNTED
    ) {
      this.performExit(this.props);
    }
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    const { status } = this.state;
    if (nextProps.transition !== this.props.transition) {
      if (nextProps.transition === transitions.enter) {
        if (
          status === transitionState.MOUNTED ||
          status === transitionState.EXITED ||
          status === transitionState.EXITING
        ) {
          this.performEnter(nextProps);
        } else if (status === transitionState.UNMOUNTED) {
          // if unmounted we need to initiate a component render and start the animation after mounted
          this.performEnterAfterUpdate = true;
          this.setState({
            status: transitionState.MOUNTED
          });
        }
      } else if (
        status === transitionState.ENTERING ||
        status === transitionState.ENTERED
      ) {
        this.performExit(nextProps);
      }
    }
  }

  componentDidUpdate() {
    if (this.performEnterAfterUpdate) {
      this.performEnter(this.props);
    }
  }

  performEnter(props) {
    // eslint-disable-next-line react/no-find-dom-node
    const node = ReactDOM.findDOMNode(this);

    // execute onEnter
    if (props.onEnter) {
      props.onEnter(node);
    }

    this.setState({ status: transitionState.ENTERING }, () => {
      // execute onEntering hook before css animation ends
      if (props.onEntering) {
        props.onEntering(node);
      }

      /** create event listener for enter css animation end */
      const onEnterTransitionEnd = event => {
        // if called by the timeout we will not have an event object
        if (event) {
          event.stopPropagation();
        }

        node.removeEventListener(
          transitionEndEvent,
          this.listeners.onEnterTransitionEnd,
          false
        );
        this.setState({ status: transitionState.ENTERED }, () => {
          // execute onEntered hook after css animation ends
          // api may change to be consistent with other animation hooks
          // currently we are passing the event to keep consistent with the h-ui api
          if (props.onEntered) {
            props.onEntered(event, node);
          }
        });
      };

      // keep reference to event listener so we can clean it up after it is triggered
      this.listeners.onEnterTransitionEnd = onEnterTransitionEnd.bind(this);
      node.addEventListener(
        transitionEndEvent,
        this.listeners.onEnterTransitionEnd
      );
    });
    // no need to trigger an additional render
    this.performEnterAfterUpdate = false;
  }

  performExit(props) {
    // eslint-disable-next-line react/no-find-dom-node
    const node = ReactDOM.findDOMNode(this);

    // execute onExit hook
    if (props.onExit) {
      props.onExit(node);
    }

    this.setState({ status: transitionState.EXITING }, () => {
      // execute onExiting hook before css animation ends
      if (props.onExiting) {
        props.onExiting(node);
      }

      /** create event listener for exit css animation end */
      const onExitTransitionEnd = event => {
        // if called by the timeout we will not have an event object
        if (event) {
          event.stopPropagation();
        }

        node.removeEventListener(
          transitionEndEvent,
          this.listeners.onExitTransitionEnd,
          false
        );
        this.setState({ status: transitionState.EXITED }, () => {
          // execute onExited hook after css animation ends
          // api may change to be consistent with other animation hooks
          // currently we are passing the event to keep consistent with the h-ui api
          if (props.onExited) {
            props.onExited(event, node);
          }

          if (props.unmountOnExit) {
            this.setState({ status: transitionState.UNMOUNTED }, () => {
              if (props.onUnmount) {
                props.onUnmount(node);
              }
            });
          }
        });
      };

      // keep reference to event listener so we can clean it up after it is triggered
      this.listeners.onExitTransitionEnd = onExitTransitionEnd.bind(this);
      node.addEventListener(
        transitionEndEvent,
        this.listeners.onExitTransitionEnd
      );
    });
  }

  render() {
    // if unmounted don't render anything
    if (this.state.status === transitionState.UNMOUNTED) {
      return null;
    }

    // remove unneeded props before passing to children
    const passProps = passthroughProps(this.props, Transition.propTypes, {
      force: ['children', 'className', 'show']
    });
    const child = React.Children.only(this.props.children);
    const childClassName = child.props.className || '';

    return React.cloneElement(child, {
      ...passProps,
      style: { ...child.props.style, ...this.props.style },
      className: childClassName
        ? `${childClassName} ${this.props.className}`
        : this.props.className,
      transitionState: this.state.status
    });
  }
}
